import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';

import type {
  FragmentType,
  GraphQL,
  HotelBookableOptionRoom,
  HotelBookableOptionWithRoomsAndDateAndPrices,
  RoomPax,
  SmartSearchImageUrl,
} from '@libs/trip-planner-interfaces';
import { useFragment } from '@libs/trip-planner-interfaces';
import { isFeatureToggleActive } from '@libs/feature-toggle';

import { assertSmartSearchList, assertSmartSearchValue } from '../../utils';
import { NoteFragment } from '../../queries';

import { toMoney } from './moneyResponseQL';
import {
  toHotelBookableOption,
  toHotelBookableRoom,
} from './hotelBookableOptionResponseQL';

export function toProductAvailabilityResponseQL(
  productAvailabilityResponseQL: GraphQL.searchProductAvailabilitySubscription
): HotelBookableOptionWithRoomsAndDateAndPrices {
  assertSmartSearchValue(productAvailabilityResponseQL.searchCheapestStay);
  const productAvailabilityResponse =
    productAvailabilityResponseQL.searchCheapestStay;

  assertSmartSearchList(productAvailabilityResponse.rooms);
  assertSmartSearchValue(productAvailabilityResponse.totalPrice);
  assertSmartSearchValue(productAvailabilityResponse.prices);
  assertSmartSearchValue(productAvailabilityResponse.prices.totalPrice);

  return {
    ...toHotelBookableOption(productAvailabilityResponse),
    date: new Date(productAvailabilityResponse.date),
    rooms: productAvailabilityResponse.rooms.map(toHotelBookableOptionRoom),
    prices: {
      totalPrice: toMoney(productAvailabilityResponse.prices.totalPrice),
      grossPrice: productAvailabilityResponse.prices.grossPrice
        ? toMoney(productAvailabilityResponse.prices.grossPrice)
        : undefined,
      isPriceWithTaxes:
        isFeatureToggleActive('show-price-with-taxes') ||
        !!productAvailabilityResponse.prices.priceIsWithTaxesIncluded,
    },
  };
}

export type ProductAvailabilityResponseQL = NonNullable<
  GraphQL.searchProductAvailabilitySubscription['searchCheapestStay']
>;
type RoomsQL = NonNullable<ProductAvailabilityResponseQL['rooms']>;
type RoomQL = NonNullable<RoomsQL[number]>;

function toHotelBookableOptionRoom(roomQL: RoomQL): HotelBookableOptionRoom {
  assertSmartSearchList(roomQL.pax);

  const childAges = roomQL.pax
    .map((p) => p.age)
    .filter<number>((age): age is number => age !== null);

  const numberOfAdults = roomQL.pax.length - childAges.length;

  const notes = roomQL.notes?.reduce((acc: string[], note) => {
    if (note) {
      acc.push(toNote(note));
    }
    return acc;
  }, []);

  const pax: RoomPax = {
    numberOfAdults,
    childAges,
  };

  assertSmartSearchList(roomQL.images);
  assertSmartSearchValue(roomQL.roomCode);

  return {
    ...toHotelBookableRoom(roomQL),
    pax,
    imageUrls: roomQL.images
      .map((image) => image.imageUrl)
      .filter((url): url is string => typeof url === 'string')
      .map((url) => url as SmartSearchImageUrl),
    roomCode: roomQL.roomCode,
    roomTypeId: roomQL.roomTypeId ?? undefined,
    roomNotes: notes || [],
  };
}

function toNote(
  noteFragmentQL: FragmentType<
    DocumentTypeDecoration<GraphQL.NoteFragment, unknown>
  >
): string {
  const note = useFragment(NoteFragment, noteFragmentQL);

  assertSmartSearchValue(note.text);

  return note.text;
}
